import type React from "react";
import { useEffect, useState } from "react";

import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { setConfigItem } from "@/features/config/configSlice";
import {
  selectTicketsSelected,
  setTicketsModalOpen,
  setTicketsSelected,
} from "../appSlice";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import Modal from "../components/modals/Modal";

import "./tickets_modal.scss";

export enum TicketType {
  ga = "general_admission",
  cg = "central_grandstand",
  mg = "main_grandstand",
  pd = "paddock",
  ph = "premium_suite",
  pg = "premium_lounge",
}

export const assignedTags = [
  {
    tags: ["GA", "MARINA"],
    ticket: TicketType.ga,
    zones: ["fan_zone", "jeddah_art_promenade", "concerts"],
  },
  {
    tags: ["CG", "MARINA"],
    ticket: TicketType.cg,
    zones: ["cg_seat", "fan_zone", "jeddah_art_promenade", "concerts"],
  },
  {
    tags: ["MG", "MARINA"],
    ticket: TicketType.mg,
    zones: ["mg_seat", "fan_zone", "jeddah_art_promenade", "concerts"],
  },
  {
    tags: ["PD", "MARINA"],
    ticket: TicketType.pd,
    zones: [
      "paddock_club",
      "fan_zone",
      "jeddah_art_promenade",
      "concerts_vip",
      "shuttle_buggy",
    ],
  },
  {
    tags: ["PH", "MARINA"],
    ticket: TicketType.ph,
    zones: [
      "meet_and_greet",
      "vip_transportation_paddock",
      "jeddah_yacht_club",
      "concerts_vip",
      "fan_zone",
      "promenade",
      "promenade_dinning",
    ],
  },
  {
    tags: ["PH", "MARINA"],
    ticket: TicketType.pg,
    zones: [
      "meet_and_greet",
      "vip_transportation_paddock",
      "jeddah_yacht_club",
      "concerts_vip",
      "fan_zone",
      "promenade",
      "promenade_dinning",
    ],
  },
];

const TicketsModal: React.FC<{
  isOpen: boolean;
  onTapOutsideModal?: () => void;
}> = ({ isOpen = false, onTapOutsideModal }) => {
  const { t } = useTranslation(["sagp", "translation"]);
  const dispatch = useAppDispatch();
  const storedTickets = useAppSelector(selectTicketsSelected);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  useEffect(() => {
    storedTickets && setSelectedOptions(storedTickets);
  }, [storedTickets]);

  const onSave = () => {
    const accTags: string[] = [];
    selectedOptions.forEach((selectedTag) => {
      const ticket = Object.values(TicketType).find((t) => t == selectedTag);
      if (ticket) {
        const tags = assignedTags.find((at) => at?.ticket == selectedTag)?.tags;
        tags?.forEach((t) => {
          !accTags.includes(t) && accTags.push(t);
        });
      }
    });

    accTags && dispatch(setConfigItem("directions.includedTags", accTags));
    dispatch(setTicketsModalOpen(false));
    dispatch(setTicketsSelected(selectedOptions));
  };

  const handleCheck = (index: number) => {
    const value = Object.values(TicketType)[index];
    setSelectedOptions((prevSelected) => {
      if (prevSelected.includes(value)) {
        return prevSelected.filter((option) => option !== value);
      } else {
        return [...prevSelected, value];
      }
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onTapOutsideModal={onTapOutsideModal}
      wrapperClassName="ticket_modal"
    >
      <Modal.Title>
        {t("modal.tickets.title", { ns: "translation" })}
      </Modal.Title>
      <Modal.Content>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          className="ticket_modal__content"
          onClick={() => {
            dispatch(setTicketsModalOpen(true));
          }}
        >
          <div className="ticket_modal__content__select">
            {Object.values(TicketType).map((ticket, index) => (
              <button
                key={`ticket_${index}`}
                className={classNames("ticket_modal__content__select__option", {
                  "ticket_modal__content__select__option--selected":
                    selectedOptions.includes(ticket),
                })}
                onClick={() => handleCheck(index)}
              >
                {t(`sagp.tickets.${ticket}`)}
              </button>
            ))}
          </div>
          {selectedOptions.length > 0 && (
            <div className="zone__access">
              {t(`sagp.access`, {
                accessList: assignedTags
                  .filter((option) => selectedOptions.includes(option.ticket))
                  .flatMap((option) => option.zones)
                  .sort()
                  .filter((value, index, array) => {
                    return array.indexOf(value) === index;
                  })
                  .map((zone) => t(`sagp.zones.${zone}`))
                  .join(", "),
              })}
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.ButtonsArea>
        <button onClick={onSave} className="panel__button">
          {t("generic.save", { ns: "translation" })}
        </button>
      </Modal.ButtonsArea>
    </Modal>
  );
};

export default TicketsModal;
